// ==================== //
// Cloudflare Turnstile - Bot Protection
// ==================== //
// Turnstile verification tokens
let contactFormTurnstileToken = null;
let leadMagnetTurnstileToken = null;

// Turnstile callback for contact form
window.onTurnstileSuccess = function(token) {
  contactFormTurnstileToken = token;
  // Enable submit button
  const submitBtn = document.getElementById('contact-submit-btn');
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.style.opacity = '1';
  }
};

// Turnstile callback for lead magnet form
window.onLeadMagnetTurnstileSuccess = function(token) {
  leadMagnetTurnstileToken = token;
};

// Turnstile error callback
window.onTurnstileError = function(error) {
  console.error('Turnstile verification failed:', error);
  alert('Bot verification failed. Please refresh the page and try again.');
};

// ==================== //
// Site Data & Initialization
// ==================== //
let siteData = null;
const CALENDLY_URL = 'https://calendly.com/adoghri-ideaboundary/30min';
const CALENDLY_EMBED_URL = `${CALENDLY_URL}?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=18e0ff`;
const GOOGLE_CALENDAR_URL = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0kwCBrs4MsOEQ_LeeVkgnTfSInW6flGj0-VQ6r387FrUz_psErdEiU6ummM8jIVW_PwfEvJpQf';

// Security: HTML sanitization helper
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Security: Create element safely
function createElementFromHTML(tag, content, attributes = {}) {
  const element = document.createElement(tag);
  if (content) {
    element.textContent = content;
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === 'class') {
      element.className = value;
    } else if (key.startsWith('data-')) {
      element.setAttribute(key, value);
    } else {
      element[key] = value;
    }
  });
  return element;
}

// Enhanced error handling utilities
// Retry mechanism with exponential backoff
async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = attempt === maxRetries - 1;
      if (isLastAttempt) {
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

// Fetch with timeout
async function fetchWithTimeout(url, options = {}, timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please check your internet connection');
    }
    throw error;
  }
}

// Safe JSON parse with error handling
function safeJSONParse(text, fallback = null) {
  try {
    return JSON.parse(text);
  } catch (error) {
    return fallback;
  }
}

// Load site data on page load - ENHANCED: Improved error handling with retries
document.addEventListener("DOMContentLoaded", async () => {
  try {
    // Read inline data - no network request needed!
    const inlineData = document.getElementById('site-data');
    if (inlineData) {
      // Use safe JSON parse for inline data
      const parsedData = safeJSONParse(inlineData.textContent);

      if (parsedData) {
        siteData = parsedData;
        renderPage();
        initializeInteractions();
      } else {
        // If inline data is invalid, try fallback
        throw new Error('Invalid inline site data format');
      }
    } else {
      // Fallback to fetch if inline data not found (with retry and timeout)
      await loadSiteDataWithRetry();
    }
  } catch (error) {
    showErrorPage(error);
  }
});

// Load site data from JSON file with retry logic
async function loadSiteDataWithRetry() {
  try {
    await retryWithBackoff(async () => {
      const response = await fetchWithTimeout('site-data.json', {}, 10000);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate that we got valid data
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid data format received');
      }

      siteData = data;
      renderPage();
      initializeInteractions();
    }, 3, 1000);
  } catch (error) {
    throw error;
  }
}

// Show comprehensive error page
function showErrorPage(error) {
  // Clear all "Loading..." placeholders
  const loadingElements = [
    'hero-headline',
    'hero-subheadline',
    'hero-badges',
    'services-grid',
    'case-studies-grid',
    'process-subtitle',
    'process-steps',
    'about-title',
    'about-headline',
    'about-bio',
    'about-philosophy-title',
    'about-principles-list',
    'tech-grid',
    'faq-container',
    'lead-magnet-headline',
    'lead-magnet-description'
  ];

  loadingElements.forEach(id => {
    const element = document.getElementById(id);
    if (element) {
      element.innerHTML = '';
    }
  });

  // Show error message in hero section
  const heroHeadline = document.getElementById('hero-headline');
  const heroSubheadline = document.getElementById('hero-subheadline');

  if (heroHeadline) {
    heroHeadline.textContent = 'Unable to Load Page';
    heroHeadline.style.color = '#ff3b30';
  }

  if (heroSubheadline) {
    heroSubheadline.innerHTML = `
      We're experiencing technical difficulties loading the page content.<br>
      Please try refreshing the page or contact us directly at
      <a href="mailto:contact@ideaboundary.com" style="color: var(--color-primary); text-decoration: underline;">contact@ideaboundary.com</a>
    `;
  }

  // Add retry button
  const heroCTA = document.querySelector('.hero-cta');
  if (heroCTA) {
    heroCTA.innerHTML = `
      <button class="btn btn-primary" onclick="location.reload()">Refresh Page</button>
      <a href="mailto:contact@ideaboundary.com" class="btn btn-secondary">Contact Us</a>
    `;
  }
}

// ==================== //
// Render Page Content
// ==================== //
function renderPage() {
  if (!siteData) return;

  renderHero();
  renderServices();
  renderCaseStudies();
  renderProcess();
  renderAbout();
  renderTechStack();
  renderFAQ();
  renderLeadMagnet();
}

// ==================== //
// Hero Section
// ==================== //
function renderHero() {
  const { hero } = siteData;

  document.getElementById('hero-headline').textContent = hero.headline;
  document.getElementById('hero-subheadline').textContent = hero.subheadline;

  const badgesContainer = document.getElementById('hero-badges');
  badgesContainer.innerHTML = ''; // Clear existing content
  hero.trust_badges.forEach(badge => {
    const badgeElement = createElementFromHTML('div', badge, { class: 'badge' });
    badgesContainer.appendChild(badgeElement);
  });
}

// ==================== //
// Case Studies Section
// ==================== //
function renderCaseStudies() {
  const { case_studies } = siteData;
  const caseStudiesGrid = document.getElementById('case-studies-grid');
  caseStudiesGrid.innerHTML = ''; // Clear existing content

  case_studies.forEach(caseStudy => {
    const card = document.createElement('div');
    card.className = 'case-study-card';

    const client = document.createElement('div');
    client.className = 'case-study-client';
    client.textContent = `${caseStudy.client} • ${caseStudy.industry}`;
    card.appendChild(client);

    const challenge = document.createElement('p');
    challenge.className = 'case-study-challenge';
    const challengeStrong = document.createElement('strong');
    challengeStrong.textContent = 'Challenge:';
    challenge.appendChild(challengeStrong);
    challenge.appendChild(document.createTextNode(' ' + caseStudy.challenge));
    card.appendChild(challenge);

    const solution = document.createElement('p');
    solution.className = 'case-study-challenge';
    const solutionStrong = document.createElement('strong');
    solutionStrong.textContent = 'Solution:';
    solution.appendChild(solutionStrong);
    solution.appendChild(document.createTextNode(' ' + caseStudy.solution));
    card.appendChild(solution);

    const resultsTitle = document.createElement('h4');
    resultsTitle.textContent = 'Results';
    resultsTitle.style.color = 'var(--color-accent-warm)';
    resultsTitle.style.marginTop = 'var(--space-6)';
    resultsTitle.style.marginBottom = 'var(--space-3)';
    card.appendChild(resultsTitle);

    const resultsList = document.createElement('ul');
    resultsList.className = 'case-study-results';
    caseStudy.results.forEach(result => {
      const li = document.createElement('li');
      // Safely highlight metrics without using innerHTML
      const parts = result.split(/(\d+%|\d+\/\d+|\d+ days?|\d+x)/g);
      parts.forEach(part => {
        if (/^(\d+%|\d+\/\d+|\d+ days?|\d+x)$/.test(part)) {
          const span = document.createElement('span');
          span.className = 'metric-highlight';
          span.textContent = part;
          li.appendChild(span);
        } else {
          li.appendChild(document.createTextNode(part));
        }
      });
      resultsList.appendChild(li);
    });
    card.appendChild(resultsList);

    const tagsContainer = document.createElement('div');
    tagsContainer.className = 'project-tags';
    tagsContainer.style.marginTop = 'var(--space-6)';
    caseStudy.tech_stack.forEach(tag => {
      const tagSpan = document.createElement('span');
      tagSpan.className = 'tag';
      tagSpan.textContent = tag;
      tagsContainer.appendChild(tagSpan);
    });
    card.appendChild(tagsContainer);

    const engagement = document.createElement('div');
    engagement.style.marginTop = 'var(--space-4)';
    engagement.style.fontSize = '0.85rem';
    engagement.style.color = 'var(--color-text-secondary)';
    engagement.textContent = `Engagement: ${caseStudy.engagement}`;
    card.appendChild(engagement);

    caseStudiesGrid.appendChild(card);
  });
}

// ==================== //
// Services Section
// ==================== //
function renderServices() {
  const { services } = siteData;
  const servicesGrid = document.getElementById('services-grid');
  servicesGrid.innerHTML = ''; // Clear existing content

  services.forEach(service => {
    const card = document.createElement('div');
    card.className = 'service-card';

    const title = document.createElement('h3');
    title.textContent = service.name;
    card.appendChild(title);

    const headline = document.createElement('p');
    headline.className = 'service-headline';
    headline.textContent = service.outcome;
    card.appendChild(headline);

    const list = document.createElement('ul');
    service.what_included.forEach(item => {
      const li = document.createElement('li');
      li.textContent = item;
      list.appendChild(li);
    });
    card.appendChild(list);

    const engagement = document.createElement('div');
    engagement.className = 'service-engagement';
    engagement.textContent = `Typical engagement: ${service.typical_engagement}`;
    card.appendChild(engagement);

    servicesGrid.appendChild(card);
  });
}

// ==================== //
// Process Section
// ==================== //
function renderProcess() {
  const { process } = siteData;
  document.getElementById('process-subtitle').textContent = process.subtitle;

  const processSteps = document.getElementById('process-steps');
  processSteps.innerHTML = ''; // Clear existing content

  process.steps.forEach(step => {
    const card = document.createElement('div');
    card.className = 'process-step';

    const number = document.createElement('div');
    number.className = 'process-number';
    number.textContent = step.number;
    card.appendChild(number);

    const title = document.createElement('h3');
    title.textContent = step.title;
    card.appendChild(title);

    const duration = document.createElement('div');
    duration.className = 'process-duration';
    duration.textContent = step.duration;
    card.appendChild(duration);

    const description = document.createElement('p');
    description.className = 'process-description';
    description.textContent = step.description;
    card.appendChild(description);

    const deliverablesTitle = document.createElement('h4');
    deliverablesTitle.textContent = 'Deliverables:';
    deliverablesTitle.style.fontSize = '0.9rem';
    deliverablesTitle.style.marginTop = 'var(--space-4)';
    deliverablesTitle.style.color = 'var(--color-text-dark)';
    card.appendChild(deliverablesTitle);

    const deliverablesList = document.createElement('ul');
    deliverablesList.className = 'process-deliverables';
    step.deliverables.forEach(deliverable => {
      const li = document.createElement('li');
      li.textContent = deliverable;
      deliverablesList.appendChild(li);
    });
    card.appendChild(deliverablesList);

    processSteps.appendChild(card);
  });
}

// ==================== //
// About Section
// ==================== //
function renderAbout() {
  const { about } = siteData;

  document.getElementById('about-title').textContent = about.section_title;
  document.getElementById('about-headline').textContent = about.headline;
  document.getElementById('about-bio').textContent = about.bio;
  document.getElementById('about-philosophy-title').textContent = about.philosophy_title;

  const principlesList = document.getElementById('about-principles-list');
  principlesList.innerHTML = ''; // Clear existing content
  about.principles.forEach(principle => {
    const li = document.createElement('li');
    // Safely parse bold text without innerHTML
    const parts = principle.split(/(\*\*.*?\*\*)/g);
    parts.forEach(part => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const strong = document.createElement('strong');
        strong.textContent = part.slice(2, -2);
        li.appendChild(strong);
      } else {
        li.appendChild(document.createTextNode(part));
      }
    });
    principlesList.appendChild(li);
  });
}

// ==================== //
// Lead Magnet Section
// ==================== //
function renderLeadMagnet() {
  const { lead_magnet } = siteData;

  document.getElementById('lead-magnet-headline').textContent = lead_magnet.headline;
  document.getElementById('lead-magnet-description').textContent = lead_magnet.description;
  document.getElementById('lead-magnet-cta').textContent = lead_magnet.cta;

  // Add lead magnet form handler with validation
  const leadMagnetForm = document.getElementById('lead-magnet-form');
  if (leadMagnetForm) {
    leadMagnetForm.addEventListener('submit', handleLeadMagnetSubmit);
  }
}

// Google Sheets Configuration
// Replace with your Google Apps Script web app URL
const GOOGLE_SHEETS_CONFIG = {
  webAppUrl: 'https://script.google.com/macros/s/AKfycbxHnv59KMCH4u-WUYPsgU2434cbvIcwV3H8Fkqr2b_fk-YI7CPMkzVEjKHKavTEp1At/exec' // Paste the URL from Step 1
};

// Save to Google Sheets via Apps Script - ENHANCED: With retry logic
async function saveToGoogleSheets(name, email, company) {
  const timestamp = new Date().toISOString();

  try {
    // Use retry mechanism for better reliability
    await retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        GOOGLE_SHEETS_CONFIG.webAppUrl,
        {
          method: 'POST',
          mode: 'no-cors', // Required for Apps Script
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            timestamp: timestamp,
            name: name,
            email: email,
            company: company || 'Not provided'
          })
        },
        8000 // 8 second timeout
      );

      // Note: no-cors mode doesn't allow reading response, so we assume success if no error
    }, 2, 1000); // 2 retries with 1 second base delay

    return true;
  } catch (error) {
    // Don't block form submission if Sheets fails - graceful degradation
    return false;
  }
}

// EmailJS Configuration
// You'll need to replace these with your actual EmailJS credentials
const EMAILJS_CONFIG = {
  publicKey: 'pAUYTzAFVr9G-o4jD', // Get from EmailJS dashboard
  serviceId: 'service_shr97w8', // Get from EmailJS dashboard
  businessTemplateId: 'template_8ssu6d6', // Template for business notification
  userTemplateId: 'template_kh08nzm' // Template for user confirmation
};

// Domain validation for EmailJS security
// Defense-in-depth: Verify forms are only submitted from authorized domains
function validateEmailJSDomain() {
  const allowedDomains = [
    'adoghri.com',
    'www.adoghri.com',
    'ideaboundary.com',
    'www.ideaboundary.com',
    'localhost',
    '127.0.0.1'
  ];

  const currentHostname = window.location.hostname;

  // Check if current domain is in the allowed list
  const isAllowed = allowedDomains.some(domain =>
    currentHostname === domain || currentHostname.endsWith('.' + domain)
  );

  if (!isAllowed) {
    return false;
  }

  return true;
}

// Lead magnet form submission handler
async function handleLeadMagnetSubmit(e) {
  e.preventDefault();

  const form = e.target;
  const formData = new FormData(form);
  const email = (formData.get('email') || '').trim();
  const name = (formData.get('name') || '').trim();
  const company = (formData.get('company') || '').trim();

  // Turnstile validation - CRITICAL BOT CHECK
  if (!leadMagnetTurnstileToken) {
    alert('Please complete the bot verification challenge.');
    return;
  }

  // Basic validation
  if (!email || !name) {
    alert('Please fill in your name and email address.');
    return;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    alert('Please enter a valid email address.');
    return;
  }

  // Enhanced honeypot check - CRITICAL BOT TRAP
  const honeypot = formData.get('_honey');
  const honeyWebsite = formData.get('website');
  const honeyConfirmEmail = formData.get('confirm_email');

  // Silent fail for bots
  if (honeypot || honeyWebsite || honeyConfirmEmail) {
    // Log bot attempt for analytics
    if (typeof gtag === 'function') {
      gtag('event', 'bot_detected', {
        event_category: 'security',
        event_label: 'lead_magnet_honeypot_triggered',
        page_location: window.location.pathname
      });
    }
    return;
  }

  // Show loading state
  const submitBtn = form.querySelector('button[type="submit"]');
  const originalText = submitBtn ? submitBtn.textContent : '';
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
  }

  try {
    // Validate domain (defense-in-depth security measure)
    if (!validateEmailJSDomain()) {
      throw new Error('This form can only be submitted from authorized domains.');
    }

    // Initialize EmailJS (only needs to be done once, but safe to call multiple times)
    if (typeof emailjs !== 'undefined') {
      emailjs.init(EMAILJS_CONFIG.publicKey);
    } else {
      throw new Error('EmailJS library not loaded. Please refresh the page and try again.');
    }

    // Prepare template parameters
    const templateParams = {
      to_name: name,
      to_email: email,
      from_name: name,
      user_email: email,
      user_name: name,
      company: company || 'Not provided',
      download_link: 'https://drive.google.com/file/d/17S7JWvb1-oxYfgESLkfTkX61brBYZFMG/view?usp=drive_link',
      reply_to: email
    };

    // Send emails with retry logic for better reliability
    await retryWithBackoff(async () => {
      // Send business notification
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.businessTemplateId,
        {
          ...templateParams,
          to_email: 'contact@ideaboundary.com'
        }
      );

      // Send user confirmation
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.userTemplateId,
        templateParams
      );
    }, 2, 1500); // 2 retries with 1.5 second base delay

    // Save to Google Sheets (non-blocking, graceful degradation)
    saveToGoogleSheets(name, email, company).catch((error) => {
      // Continue anyway - this is not critical for user experience
    });

    // Success - reset form, Turnstile, and show modal
    form.reset();
    leadMagnetTurnstileToken = null;

    // Reset Turnstile widget
    if (typeof turnstile !== 'undefined') {
      turnstile.reset('#lead-magnet-turnstile');
    }

    showLeadMagnetModal(email);

  } catch (error) {

    // Provide specific error messages based on error type
    let errorMessage = 'Sorry, there was an error sending your request. ';

    if (error.message && error.message.includes('library not loaded')) {
      errorMessage += 'Please refresh the page and try again.';
    } else if (error.message && error.message.includes('timeout')) {
      errorMessage += 'The request timed out. Please check your internet connection and try again.';
    } else {
      errorMessage += 'Please try again or email us directly at contact@ideaboundary.com';
    }

    alert(errorMessage);
  } finally {
    // Restore button state
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = originalText || 'Download Free Checklist';
    }
  }
}

// ==================== //
// Tech Stack Section
// ==================== //
function renderTechStack() {
  const { tech_stack } = siteData;
  const techGrid = document.getElementById('tech-grid');
  techGrid.innerHTML = ''; // Clear existing content

  // Iterate through each category
  Object.entries(tech_stack).forEach(([categoryName, items]) => {
    // Create category container
    const categoryDiv = document.createElement('div');
    categoryDiv.className = 'tech-category';

    // Create category title
    const categoryTitle = document.createElement('h4');
    categoryTitle.className = 'tech-category-title';
    categoryTitle.textContent = categoryName;
    categoryDiv.appendChild(categoryTitle);

    // Create tags container for this category
    const tagsContainer = document.createElement('div');
    tagsContainer.className = 'tech-tags';

    // Add each tech item as a tag
    items.forEach(item => {
      const tag = document.createElement('span');
      tag.className = 'tech-tag';
      tag.textContent = item;
      tagsContainer.appendChild(tag);
    });

    categoryDiv.appendChild(tagsContainer);
    techGrid.appendChild(categoryDiv);
  });
}

// ==================== //
// FAQ Section
// ==================== //
function renderFAQ() {
  const { faq } = siteData;
  const faqContainer = document.getElementById('faq-container');
  faqContainer.innerHTML = ''; // Clear existing content

  faq.forEach((item, index) => {
    const faqItem = document.createElement('div');
    faqItem.className = 'faq-item';
    faqItem.setAttribute('data-faq-index', index);

    const question = document.createElement('div');
    question.className = 'faq-question';
    question.textContent = item.q;
    faqItem.appendChild(question);

    const answer = document.createElement('div');
    answer.className = 'faq-answer';
    answer.textContent = item.a;
    faqItem.appendChild(answer);

    // Add click handler for this FAQ item
    faqItem.addEventListener('click', () => {
      faqItem.classList.toggle('active');
    });

    faqContainer.appendChild(faqItem);
  });
}

// ==================== //
// Interactive Features
// ==================== //
function initializeInteractions() {
  // Mobile navigation toggle
  const hamburger = document.getElementById('hamburger');
  const navMenu = document.getElementById('nav-menu');

  if (hamburger) {
    hamburger.addEventListener('click', () => {
      const isExpanded = navMenu.classList.toggle('active');
      hamburger.setAttribute('aria-expanded', isExpanded.toString());
    });
  }

  // Close mobile menu when clicking a link
  document.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', () => {
      navMenu.classList.remove('active');
      if (hamburger) {
        hamburger.setAttribute('aria-expanded', 'false');
      }
    });
  });

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      if (this.classList.contains('book-call-trigger')) return;

      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        const offsetTop = target.offsetTop - 80; // Account for fixed navbar
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    });
  });

  // Setup form email obfuscation
  setupFormEmail();

  // Booking CTA controls
  initBookingCTAListeners();

  // Contact form submission
  const contactForm = document.getElementById('contact-form');
  if (contactForm) {
    // Track form load time for timing-based bot detection
    contactForm.dataset.loadTime = Date.now().toString();
    contactForm.addEventListener('submit', handleFormSubmit);

    // Disable submit button until Turnstile is verified
    const submitBtn = document.getElementById('contact-submit-btn');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.5';
      submitBtn.title = 'Complete bot verification to submit';
    }

    // Track honeypot interactions (bots often interact with hidden fields)
    const honeypotFields = contactForm.querySelectorAll('[name="_honey"], [name="website"]');
    honeypotFields.forEach(field => {
      field.addEventListener('focus', () => {
        honeypotInteractions.set('focus', (honeypotInteractions.get('focus') || 0) + 1);
      });
      field.addEventListener('input', () => {
        honeypotInteractions.set('input', (honeypotInteractions.get('input') || 0) + 1);
      });
    });
  }
}

// ==================== //
// Form Handling
// ==================== //

// Email obfuscation - Decode base64 email on page load
function setupFormEmail() {
  const contactForm = document.getElementById('contact-form');
  if (contactForm && contactForm.dataset.email) {
    const decodedEmail = atob(contactForm.dataset.email);
    contactForm.action = `https://formsubmit.co/${decodedEmail}`;
  }
}

// Security: Rate limiting for form submissions (client-side)
// NOTE: This is client-side only and can be bypassed. For production, implement
// server-side rate limiting using:
// - IP-based throttling (max 5 requests per IP per hour)
// - Token bucket algorithm for API endpoints
// - Consider using services like Cloudflare Rate Limiting or Nginx rate limiting
let lastSubmitTime = 0;
const SUBMIT_COOLDOWN = 5000; // 5 seconds between submissions

// Enhanced honeypot tracking
const honeypotInteractions = new Map();
const MAX_HONEYPOT_INTERACTIONS = 0; // Bots should NEVER interact with honeypot

// Security: Input validation and sanitization
function validateAndSanitizeInput(value, type = 'text') {
  if (!value || typeof value !== 'string') return '';

  // Trim whitespace
  value = value.trim();

  // Type-specific validation
  switch(type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        throw new Error('Invalid email address');
      }
      break;
    case 'text':
      // Remove any HTML tags for security
      value = value.replace(/<[^>]*>/g, '');
      break;
  }

  // Maximum length check
  if (value.length > 5000) {
    throw new Error('Input too long');
  }

  return value;
}

async function handleFormSubmit(e) {
  e.preventDefault(); // Always prevent default, we'll handle submission via AJAX

  // Turnstile validation - CRITICAL BOT CHECK
  if (!contactFormTurnstileToken) {
    showModal('error', 'Verification Required', 'Please complete the bot verification challenge.');
    return;
  }

  // Rate limiting check
  const now = Date.now();
  if (now - lastSubmitTime < SUBMIT_COOLDOWN) {
    showModal('error', 'Please wait', 'Please wait a few seconds before submitting again.');
    return;
  }

  const form = e.target;
  const formData = new FormData(form);
  const data = {};

  try {
    // Validate and sanitize all inputs
    data.name = validateAndSanitizeInput(formData.get('name'), 'text');
    data.email = validateAndSanitizeInput(formData.get('email'), 'email');
    data.company = validateAndSanitizeInput(formData.get('company'), 'text');
    data.role = validateAndSanitizeInput(formData.get('role'), 'text');
    data.project_type = validateAndSanitizeInput(formData.get('project_type'), 'text');
    data.description = validateAndSanitizeInput(formData.get('description'), 'text');
    data.budget = validateAndSanitizeInput(formData.get('budget'), 'text');
    data.timeline = validateAndSanitizeInput(formData.get('timeline'), 'text');

    // Enhanced honeypot check (anti-spam) - CRITICAL BOT TRAP
    const honeypot = formData.get('website');
    const honeyEmail = formData.get('_honey');
    const honeyConfirmEmail = formData.get('confirm_email');
    const honeyPhone = formData.get('phone_number');

    // Check if any honeypot field was filled - silent fail for bots
    if (honeypot || honeyEmail || honeyConfirmEmail || honeyPhone) {
      // Log bot attempt for analytics (optional)
      if (typeof gtag === 'function') {
        gtag('event', 'bot_detected', {
          event_category: 'security',
          event_label: 'honeypot_triggered',
          page_location: window.location.pathname
        });
      }
      // Silent fail - don't show any error to bot
      return;
    }

    // Check timing - bots often submit forms too quickly
    const formElement = e.target;
    const formLoadTime = formElement.dataset.loadTime || Date.now();
    const timeDiff = Date.now() - parseInt(formLoadTime);
    if (timeDiff < 3000) {
      return; // Human users take at least 3 seconds to fill a form
    }

    // Required fields check
    if (!data.name || !data.email || !data.project_type || !data.description) {
      showModal('error', 'Missing Information', 'Please fill in all required fields.');
      return;
    }

    // Update last submit time
    lastSubmitTime = now;

    // Track form submission attempt (Analytics)
    if (typeof gtag === 'function') {
      gtag('event', 'form_submit_attempt', {
        project_type: data.project_type || 'unknown',
        budget: data.budget || 'unknown',
        timeline: data.timeline || 'unknown',
        has_company: !!data.company,
        page_location: window.location.pathname
      });
    }

    // Submit via AJAX to FormSubmit with retry logic
    try {
      await retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          form.action,
          {
            method: 'POST',
            body: formData,
            headers: {
              'Accept': 'application/json'
            }
          },
          15000 // 15 second timeout
        );

        if (!response.ok) {
          throw new Error(`Form submission failed with status: ${response.status}`);
        }

        return response;
      }, 2, 2000); // 2 retries with 2 second base delay

      // Show success modal
      showSuccessModal();
      // Reset form and Turnstile
      form.reset();
      contactFormTurnstileToken = null;

      // Reset Turnstile widget
      if (typeof turnstile !== 'undefined') {
        turnstile.reset('#contact-turnstile');
      }

    } catch (error) {

      let errorMessage = 'Sorry, there was an error submitting your form. ';

      if (error.message && error.message.includes('timeout')) {
        errorMessage += 'The request timed out. Please check your internet connection and try again.';
      } else {
        errorMessage += 'Please try again or email me directly at contact@ideaboundary.com';
      }

      showModal('error', 'Submission Error', errorMessage);
    }

  } catch (error) {
    showModal('error', 'Validation Error', error.message || 'Please check your input and try again.');
  }
}

// Booking modal controls
function openBookingModal(ctaLocation = 'unknown') {
  const bookingModal = document.getElementById('booking-modal');

  // Track the CTA click + modal view together to avoid double counting
  trackCTAClick('book_call', ctaLocation);
  trackEvent('call_booking_modal_opened', {
    cta_location: ctaLocation,
    page_location: window.location.pathname
  });

  if (bookingModal) {
    bookingModal.style.display = 'flex';
    requestAnimationFrame(() => bookingModal.classList.add('active'));

    // Ensure embed src is set (in case of deferred load)
    const bookingFrame = document.getElementById('booking-embed-frame');
    if (bookingFrame && !bookingFrame.src) {
      bookingFrame.src = CALENDLY_EMBED_URL;
    }
    return;
  }

  // Fallback if modal is not present
  launchCalendlyPopup('missing_modal');
}

function closeBookingModal() {
  const bookingModal = document.getElementById('booking-modal');
  if (bookingModal) {
    bookingModal.classList.remove('active');
    setTimeout(() => bookingModal.style.display = 'none', 300);
  }
}

function launchCalendlyPopup(source = 'booking_modal') {
  try {
    if (window.Calendly && typeof Calendly.initPopupWidget === 'function') {
      Calendly.initPopupWidget({ url: CALENDLY_URL });
      trackEvent('calendly_popup_launched', {
        source,
        page_location: window.location.pathname
      });
      return;
    }

    // Popup script missing, open new tab as fallback
    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    trackEvent('calendly_popup_fallback', {
      source,
      fallback: 'new_tab',
      page_location: window.location.pathname
    });
  } catch (error) {
    // Final fallback to Google Calendar link
    window.open(GOOGLE_CALENDAR_URL, '_blank', 'noopener,noreferrer');
    trackEvent('call_booking_fallback', {
      source,
      fallback: 'google_calendar',
      error: error.message,
      page_location: window.location.pathname
    });
  }
}

function initBookingCTAListeners() {
  const bookingButtons = document.querySelectorAll('.book-call-trigger');

  bookingButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const ctaLocation = btn.dataset.ctaLocation || btn.closest('section')?.id || 'unknown';
      openBookingModal(ctaLocation);
    });
  });

  const bookingClose = document.getElementById('booking-close');
  const bookingOverlay = document.getElementById('booking-modal-overlay');
  if (bookingClose) bookingClose.addEventListener('click', closeBookingModal);
  if (bookingOverlay) bookingOverlay.addEventListener('click', closeBookingModal);

  const bookingCalendly = document.getElementById('booking-open-calendly');
  if (bookingCalendly) {
    bookingCalendly.addEventListener('click', (e) => {
      e.preventDefault();
      launchCalendlyPopup('booking_modal_link');
    });
  }

  const bookingGoogle = document.getElementById('booking-google-calendar');
  if (bookingGoogle) {
    bookingGoogle.addEventListener('click', () => {
      trackEvent('call_booking_google_link', {
        source: 'booking_modal',
        page_location: window.location.pathname
      });
    });
  }
}

// Modal functions
function showSuccessModal() {
  // Track successful form submission as conversion (Analytics)
  if (typeof gtag === 'function') {
    gtag('event', 'form_submit_success', {
      event_category: 'conversion',
      event_label: 'contact_form',
      value: 1
    });

    // Track as GA4 recommended conversion event
    gtag('event', 'generate_lead', {
      currency: 'USD',
      value: 100 // Estimated lead value
    });
  }

  const modal = document.getElementById('success-modal');
  if (modal) {
    modal.style.display = 'flex';
    setTimeout(() => modal.classList.add('active'), 10);
  }
}

function showLeadMagnetModal(email) {
  const modal = document.getElementById('lead-magnet-modal');
  const emailTarget = document.getElementById('lead-magnet-email-target');

  if (emailTarget) {
    emailTarget.textContent = email || 'your inbox';
  }

  if (modal) {
    modal.style.display = 'flex';
    setTimeout(() => modal.classList.add('active'), 10);
  }
}

function closeModal() {
  const modal = document.getElementById('success-modal');
  if (modal) {
    modal.classList.remove('active');
    setTimeout(() => modal.style.display = 'none', 300);
  }
}

function closeLeadMagnetModal() {
  const modal = document.getElementById('lead-magnet-modal');
  if (modal) {
    modal.classList.remove('active');
    setTimeout(() => modal.style.display = 'none', 300);
  }
}

function showModal(type, title, message) {
  // For now, use alert for error modals (can be styled later if needed)
  alert(`${title}\n\n${message}`);
}

// Modal close handlers
document.addEventListener('DOMContentLoaded', () => {
  const modalClose = document.getElementById('modal-close');
  const modalOverlay = document.getElementById('modal-overlay');
  const modalCalendlyLink = document.getElementById('modal-calendly-link');
  const modalEmail = document.getElementById('modal-email');
  const copyEmailBtn = document.getElementById('copy-email-btn');
  const leadMagnetClose = document.getElementById('lead-magnet-close');
  const leadMagnetOverlay = document.getElementById('lead-magnet-overlay');

  if (modalClose) {
    modalClose.addEventListener('click', closeModal);
  }

  if (modalOverlay) {
    modalOverlay.addEventListener('click', closeModal);
  }

  if (leadMagnetClose) {
    leadMagnetClose.addEventListener('click', closeLeadMagnetModal);
  }

  if (leadMagnetOverlay) {
    leadMagnetOverlay.addEventListener('click', closeLeadMagnetModal);
  }

  // Close modal and open Calendly
  if (modalCalendlyLink) {
    modalCalendlyLink.addEventListener('click', (e) => {
      e.preventDefault();
      closeModal();
      // Small delay to let modal close animation finish
      setTimeout(() => {
        trackCTAClick('book_call', 'contact_success_modal');
        launchCalendlyPopup('contact_success_modal');
      }, 300);
    });
  }

  // Copy email functionality (obfuscated)
  const copyEmailToClipboard = async () => {
    // Decode obfuscated email (base64)
    const email = atob('Y29udGFjdEBpZGVhYm91bmRhcnkuY29t');
    try {
      await navigator.clipboard.writeText(email);
      showCopyFeedback();
    } catch (err) {
      // Fallback for older browsers
      fallbackCopyEmail(email);
    }
  };

  // Show visual feedback when email is copied
  const showCopyFeedback = () => {
    const copyBtn = document.getElementById('copy-email-btn');
    const originalHTML = copyBtn.innerHTML;

    // Change to checkmark
    copyBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    copyBtn.style.color = 'var(--color-accent-warm)';

    // Reset after 2 seconds
    setTimeout(() => {
      copyBtn.innerHTML = originalHTML;
      copyBtn.style.color = '';
    }, 2000);
  };

  // Fallback copy method for older browsers
  const fallbackCopyEmail = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      showCopyFeedback();
    } catch (err) {
    }
    document.body.removeChild(textArea);
  };

  // Add click handlers for email copying
  if (modalEmail) {
    modalEmail.addEventListener('click', copyEmailToClipboard);
  }

  if (copyEmailBtn) {
    copyEmailBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      copyEmailToClipboard();
    });
  }

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  });
});

// ==================== //
// Utility Functions
// ==================== //

// OPTIMIZED: Enhanced Intersection Observer with better performance
function initScrollAnimations() {
  // Use a single observer with better options
  const observerOptions = {
    threshold: [0.1, 0.15],
    rootMargin: '0px 0px -60px 0px'
  };

  // Single observer for all animations
  const scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const isCard = element.classList.contains('role-card') ||
                      element.classList.contains('service-card') ||
                      element.classList.contains('case-study-card') ||
                      element.classList.contains('process-step') ||
                      element.classList.contains('faq-item');

        if (isCard) {
          // Stagger animation based on position
          const siblings = Array.from(element.parentElement.children);
          const delay = siblings.indexOf(element) * 80;
          setTimeout(() => {
            element.classList.add('fade-in');
          }, delay);
        } else {
          // Simple fade-in for sections
          setTimeout(() => {
            element.classList.add('visible');
          }, 50);
        }

        scrollObserver.unobserve(element);
      }
    });
  }, observerOptions);

  // Batch observe all elements
  const elementsToObserve = [
    ...document.querySelectorAll('.section'),
    ...document.querySelectorAll('.section-title'),
    ...document.querySelectorAll('.role-card, .service-card, .case-study-card, .process-step, .pricing-card, .lead-magnet-card, .faq-item'),
    ...document.querySelectorAll('.approach-philosophy, .approach-principles'),
    document.querySelector('.contact-form')
  ].filter(Boolean);

  // Use requestIdleCallback for non-critical observations
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      elementsToObserve.forEach(el => scrollObserver.observe(el));
    }, { timeout: 500 });
  } else {
    elementsToObserve.forEach(el => scrollObserver.observe(el));
  }

  // Simplified tech tag animation
  const techTags = document.querySelectorAll('.tech-tag');
  if (techTags.length > 0) {
    const techObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0) scale(1)';
          techObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    techTags.forEach((tag, index) => {
      tag.style.opacity = '0';
      tag.style.transform = 'translateY(20px) scale(0.9)';
      tag.style.transition = `all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1) ${index * 20}ms`;
      techObserver.observe(tag);
    });
  }
}

// Enhanced navbar behavior with hide on scroll down, show on scroll up
let lastScrollTop = 0;
let scrollTimeout;

function initEnhancedNavbar() {
  window.addEventListener('scroll', () => {
    const navbar = document.getElementById('navbar');
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Add scrolled class
    if (scrollTop > 50) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }

    // Hide/show navbar on scroll
    if (scrollTop > 300) {
      if (scrollTop > lastScrollTop) {
        // Scrolling down
        navbar.classList.add('hidden');
      } else {
        // Scrolling up
        navbar.classList.remove('hidden');
      }
    } else {
      navbar.classList.remove('hidden');
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }, { passive: true });
}

// Enhanced parallax effect for hero section with Apple-style smoothness
function initParallaxEffect() {
  const hero = document.querySelector('.hero');
  if (!hero) return;

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const scrolled = window.pageYOffset;
        const heroContent = hero.querySelector('.hero-content');
        const heroBg = hero.querySelector('::before');

        if (scrolled < window.innerHeight) {
          // Subtle parallax with Apple-style easing
          heroContent.style.transform = `translateY(${scrolled * 0.25}px)`;
          heroContent.style.opacity = Math.max(0.3, 1 - (scrolled / window.innerHeight) * 0.6);
        }

        ticking = false;
      });

      ticking = true;
    }
  }, { passive: true });
}

// Add magnetic effect to buttons (Apple-style interaction)
function initMagneticButtons() {
  const buttons = document.querySelectorAll('.btn');

  buttons.forEach(button => {
    button.addEventListener('mousemove', (e) => {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      // Subtle magnetic pull
      button.style.transform = `translate(${x * 0.1}px, ${y * 0.1}px) scale(1.02)`;
    });

    button.addEventListener('mouseleave', () => {
      button.style.transform = 'translate(0, 0) scale(1)';
    });
  });
}

// Add smooth hover effect for cards
function initCardInteractions() {
  const cards = document.querySelectorAll('.role-card, .service-card, .project-card, .case-study-card, .process-step, .pricing-card');

  cards.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Calculate rotation based on mouse position
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-6px) scale(1.01)`;
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
    });
  });
}

// OPTIMIZED: Initialize animations with requestIdleCallback for better performance
if ('requestIdleCallback' in window) {
  // Use requestIdleCallback for non-critical animations
  requestIdleCallback(() => {
    initScrollAnimations();
    initMagneticButtons();
    initCardInteractions();
  }, { timeout: 200 });

  // Initialize critical scroll features immediately
  initEnhancedNavbar();
  initParallaxEffect();
} else {
  // Fallback for browsers without requestIdleCallback
  setTimeout(() => {
    initScrollAnimations();
    initEnhancedNavbar();
    initParallaxEffect();
    initMagneticButtons();
    initCardInteractions();
  }, 100);
}

// Add smooth scroll behavior for better UX
document.documentElement.style.scrollBehavior = 'smooth';

// Performance optimization: Reduce motion for users who prefer it
if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.documentElement.style.scrollBehavior = 'auto';
  // Disable all animations
  document.querySelectorAll('*').forEach(el => {
    el.style.animation = 'none';
    el.style.transition = 'none';
  });
}

// ==================== //
// Google Analytics Enhanced Tracking
// ==================== //

// Utility: Safe gtag wrapper
function trackEvent(eventName, eventParams = {}) {
  if (typeof gtag === 'function') {
    gtag('event', eventName, eventParams);
  }
}

// Track scroll depth
let scrollMilestones = { 25: false, 50: false, 75: false, 100: false };
function trackScrollDepth() {
  const scrollPercent = Math.round((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100);

  Object.keys(scrollMilestones).forEach(milestone => {
    if (scrollPercent >= milestone && !scrollMilestones[milestone]) {
      scrollMilestones[milestone] = true;
      trackEvent('scroll_depth', {
        percent: milestone,
        page_location: window.location.pathname
      });
    }
  });
}

// Track time on page milestones
let timeMilestones = { 30: false, 60: false, 120: false, 300: false };
const pageLoadTime = Date.now();

function trackTimeOnPage() {
  const secondsOnPage = Math.floor((Date.now() - pageLoadTime) / 1000);

  Object.keys(timeMilestones).forEach(milestone => {
    if (secondsOnPage >= milestone && !timeMilestones[milestone]) {
      timeMilestones[milestone] = true;
      trackEvent('time_on_page', {
        seconds: milestone,
        page_location: window.location.pathname
      });
    }
  });
}

// Track outbound links
function trackOutboundLink(url, linkType) {
  trackEvent('click_outbound', {
    link_url: url,
    link_type: linkType,
    page_location: window.location.pathname
  });
}

// Track CTA clicks
function trackCTAClick(ctaName, ctaLocation) {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    page_location: window.location.pathname
  });
}

// Track form interactions
function trackFormInteraction(fieldName, action) {
  trackEvent('form_interaction', {
    field_name: fieldName,
    action: action,
    page_location: window.location.pathname
  });
}

// OPTIMIZED: Debounce helper for better performance
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initialize analytics tracking
function initAnalytics() {
  // OPTIMIZED: Scroll depth tracking with debouncing
  const debouncedScrollDepth = debounce(trackScrollDepth, 300);
  window.addEventListener('scroll', debouncedScrollDepth, { passive: true });

  // Time on page tracking
  setInterval(trackTimeOnPage, 10000); // Check every 10 seconds

  // Track page view
  trackEvent('page_view', {
    page_title: document.title,
    page_location: window.location.pathname
  });

  // Track outbound links (LinkedIn, GitHub, etc.)
  document.querySelectorAll('a[href*="linkedin.com"], a[href*="github.com"], a[href*="drive.google.com"]').forEach(link => {
    link.addEventListener('click', () => {
      const url = link.href;
      let linkType = 'other';
      if (url.includes('linkedin.com')) linkType = 'linkedin';
      else if (url.includes('github.com')) linkType = 'github';
      else if (url.includes('drive.google.com')) linkType = 'resume_download';

      trackOutboundLink(url, linkType);
    });
  });

  // Track FAQ expansions
  document.querySelectorAll('.faq-item').forEach(faq => {
    faq.addEventListener('click', () => {
      const question = faq.querySelector('.faq-question')?.textContent || 'unknown';
      trackEvent('faq_expand', {
        question: question.substring(0, 100),
        page_location: window.location.pathname
      });
    });
  });

  // Track email copy clicks
  const emailElements = document.querySelectorAll('#modal-email, #copy-email-btn, .clickable-email');
  emailElements.forEach(el => {
    el.addEventListener('click', () => {
      trackEvent('email_copy_click', {
        element: el.id || el.className,
        page_location: window.location.pathname
      });
    });
  });

  // Track form field focus (engagement)
  document.querySelectorAll('#contact-form input, #contact-form textarea, #contact-form select').forEach(field => {
    let focusTracked = false;
    field.addEventListener('focus', () => {
      if (!focusTracked) {
        focusTracked = true;
        trackFormInteraction(field.name || field.id, 'focus');
      }
    });
  });

  // Track navigation clicks
  document.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', () => {
      trackEvent('navigation_click', {
        link_text: link.textContent.trim(),
        link_url: link.href,
        page_location: window.location.pathname
      });
    });
  });

  // ==================== //
  // ENHANCED BUSINESS-CRITICAL TRACKING
  // ==================== //

  // OPTIMIZED: Track service card clicks with single observer
  const serviceCards = document.querySelectorAll('.service-card');
  if (serviceCards.length > 0) {
    // Add click handlers
    serviceCards.forEach(card => {
      card.addEventListener('click', () => {
        const serviceName = card.querySelector('h3')?.textContent || 'unknown';
        trackEvent('service_card_click', {
          event_category: 'engagement',
          event_label: serviceName,
          service_name: serviceName,
          page_location: window.location.pathname
        });
      });
    });

    // Single observer for all service cards
    const serviceObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const serviceName = entry.target.querySelector('h3')?.textContent || 'unknown';
          trackEvent('service_card_view', {
            event_category: 'engagement',
            event_label: serviceName,
            service_name: serviceName,
            page_location: window.location.pathname
          });
          serviceObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    serviceCards.forEach(card => serviceObserver.observe(card));
  }

  // OPTIMIZED: Track case study engagement with single observer
  const caseStudyCards = document.querySelectorAll('.case-study-card');
  if (caseStudyCards.length > 0) {
    // Add click handlers
    caseStudyCards.forEach(card => {
      const clientName = card.querySelector('.case-study-client')?.textContent || 'unknown';
      card.addEventListener('click', () => {
        trackEvent('case_study_click', {
          event_category: 'engagement',
          event_label: clientName,
          client: clientName,
          page_location: window.location.pathname
        });
      });
    });

    // Single observer for all case study cards
    const caseStudyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const clientName = entry.target.querySelector('.case-study-client')?.textContent || 'unknown';
          trackEvent('case_study_view', {
            event_category: 'engagement',
            event_label: clientName,
            client: clientName,
            page_location: window.location.pathname
          });
          caseStudyObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    caseStudyCards.forEach(card => caseStudyObserver.observe(card));
  }

  // Track Calendly widget opens (HIGH PRIORITY - strong conversion signal)
  // Note: This requires Calendly callback, adding global listener
  window.addEventListener('message', (e) => {
    if (e.data.event && e.data.event.indexOf('calendly') === 0) {
      if (e.data.event === 'calendly.event_type_viewed') {
        trackEvent('calendly_opened', {
          event_category: 'conversion',
          event_label: 'calendly_widget_opened',
          page_location: window.location.pathname
        });
      }
      if (e.data.event === 'calendly.event_scheduled') {
        trackEvent('calendly_scheduled', {
          event_category: 'conversion',
          event_label: 'meeting_scheduled',
          value: 500, // High value conversion
          page_location: window.location.pathname
        });
        // Also track as GA4 recommended conversion
        trackEvent('schedule_meeting', {
          currency: 'USD',
          value: 500
        });
      }
    }
  });

  // Track lead magnet downloads (HIGH PRIORITY - lead generation)
  const leadMagnetForm = document.getElementById('lead-magnet-form');
  if (leadMagnetForm) {
    leadMagnetForm.addEventListener('submit', () => {
      trackEvent('lead_magnet_download', {
        event_category: 'conversion',
        event_label: 'llm_production_checklist',
        value: 50, // Estimated lead value
        page_location: window.location.pathname
      });
    });
  }

  // Track mobile menu usage (UX insights)
  const hamburger = document.getElementById('hamburger');
  if (hamburger) {
    hamburger.addEventListener('click', () => {
      const isOpening = !document.getElementById('nav-menu')?.classList.contains('active');
      trackEvent('mobile_menu_toggle', {
        event_category: 'engagement',
        action: isOpening ? 'open' : 'close',
        page_location: window.location.pathname
      });
    });
  }

  // Track critical section visibility (content engagement)
  const criticalSections = [
    { selector: '#services', name: 'services_section' },
    { selector: '#case-studies', name: 'case_studies_section' },
    { selector: '#process', name: 'process_section' },
    { selector: '#about', name: 'about_section' },
    { selector: '#tech-stack', name: 'tech_stack_section' },
    { selector: '#contact', name: 'contact_section' }
  ];

  criticalSections.forEach(({ selector, name }) => {
    const section = document.querySelector(selector);
    if (section) {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent('section_view', {
              event_category: 'engagement',
              event_label: name,
              section_name: name,
              page_location: window.location.pathname
            });
            sectionObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.3 }); // 30% visible counts as view
      sectionObserver.observe(section);
    }
  });

  // OPTIMIZED: Track process step visibility with single observer
  const processSteps = document.querySelectorAll('.process-step');
  if (processSteps.length > 0) {
    const stepObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Array.from(processSteps).indexOf(entry.target);
          const stepTitle = entry.target.querySelector('h3')?.textContent || `step_${index + 1}`;
          trackEvent('process_step_view', {
            event_category: 'engagement',
            event_label: stepTitle,
            step_number: index + 1,
            page_location: window.location.pathname
          });
          stepObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    processSteps.forEach(step => stepObserver.observe(step));
  }

  // Track tech stack interest (capability validation)
  const techStackSection = document.getElementById('tech-stack');
  if (techStackSection) {
    const techObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('tech_stack_view', {
            event_category: 'engagement',
            event_label: 'tech_stack_viewed',
            page_location: window.location.pathname
          });
          techObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    techObserver.observe(techStackSection);
  }

  // Track hero CTA section visibility (conversion opportunity)
  const heroCTA = document.querySelector('.hero-cta');
  if (heroCTA) {
    const ctaObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('hero_cta_view', {
            event_category: 'engagement',
            event_label: 'hero_cta_visible',
            page_location: window.location.pathname
          });
          ctaObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.8 });
    ctaObserver.observe(heroCTA);
  }
}

// Initialize analytics after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnalytics);
} else {
  initAnalytics();
}
